import { Component, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ComplaintAppointment, ComplaintAppointmentWithComplaintDetails, ComplaintData, PersonsService } from '@icc/b2b/shared';
import { ConfigService, TranslateService } from '@icc/shared';
import { ComplaintService } from '../complaint.service';
import { Router } from '@angular/router';
import { Loader } from '@googlemaps/js-api-loader';
import { MapGeocoder } from '@angular/google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { NgxPermissionsService } from 'ngx-permissions';
import { Subscription } from 'rxjs';
import { OverlappingMarkerSpiderfier } from 'ts-overlapping-marker-spiderfier';

@Component({
  selector: 'icc-complaint-appointment-map',
  templateUrl: './complaint-appointment-map.component.html',
  styleUrls: ['./complaint-appointment-map.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ComplaintAppointmentMapComponent implements OnInit, OnChanges, OnDestroy {
    data: ComplaintData = {} as ComplaintData;

    appointments: ComplaintAppointment[] = [];

    hasAccessTo = {
        manageComplaint: false,
    };

    private subscriptions: Subscription[] = [];

    apiLoaded = false;
    mapZoom: google.maps.MapOptions['zoom'] = 7;

    mapCenter: google.maps.LatLngLiteral = {
        lat: 52.18799140660359,
        lng: 19.22466337395079,
    };

    complaintAppointmentsWithCoords: { complaintAppointment: ComplaintAppointmentWithComplaintDetails, location: google.maps.LatLng }[] = [];

    @ViewChild('complaintsMap', { static: false }) complaintsMap;
    @ViewChild('infoWindowTemplate', { static: true }) infoWindowTemplate: TemplateRef<any>;

    markers: google.maps.Marker[] = [];
    markerCluster: MarkerClusterer | null = null;

    @Input() filteredAppointments: ComplaintAppointmentWithComplaintDetails[] = [];
    @Input() resetMapZoomAndCenter: boolean;

    constructor(
        private router: Router,
        private complaintService: ComplaintService,
        private geocoder: MapGeocoder,
        private renderer: Renderer2,
        private translateService: TranslateService,
        private permissionsService: NgxPermissionsService,
        private personsService: PersonsService,
        private configService: ConfigService,
    ) {
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
    }

    async ngOnInit() {
        await this.loadData();
        const loader = new Loader({
            apiKey: this.data.googleMapsApiKey,
            version: 'weekly',
            libraries: ['marker'],
        });
        loader.load().then(() => {
            this.apiLoaded = true;
            if (!this.resetMapZoomAndCenter) {
                this.setDefaultView();
            }
            this.getComplaintAppointmentsWithCoords();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if(this.apiLoaded && changes.filteredAppointments) {
            this.getComplaintAppointmentsWithCoords();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    async loadData() {
        await this.complaintService.loaded;
        this.data = this.complaintService.data;
    }

    setDefaultView() {
        const storedCenter = sessionStorage.getItem(`complaints-appointments-map-center-${
            this.configService?.user?.user?.id
        }`);
        const storedZoom = sessionStorage.getItem(`complaints-appointments-map-zoom-${
            this.configService?.user?.user?.id
        }`);

        if (storedCenter) {
            this.mapCenter = JSON.parse(storedCenter);
        }

        if (storedZoom) {
            this.mapZoom = JSON.parse(storedZoom);
        }
    }

    async getComplaintAppointmentsWithCoords() {
        const addressPromises = this.filteredAppointments
            .filter((complaintAppointment) => complaintAppointment.installationAddress)
            .map(async (complaintAppointment) => {
                try {
                    const result = await this.geocoder.geocode({ address: complaintAppointment.installationAddress }).toPromise();
                    if (result?.results.length > 0) {
                        return { complaintAppointment, location: result.results[0].geometry.location };
                    } else {
                        console.error('Geocode was not successful for the following reason: No results found');
                        return null;
                    }
                } catch (error) {
                    console.error('Geocode was not successful for the following reason:', error);
                    return null;
                }
            });

            const locations = await Promise.all(addressPromises);
            this.complaintAppointmentsWithCoords = locations.filter((item) => item !== null) as { complaintAppointment: ComplaintAppointmentWithComplaintDetails, location: google.maps.LatLng }[];
            this.updateMapMarkers();
    }

    buildContent(complaintAppointment: ComplaintAppointmentWithComplaintDetails): HTMLElement {
        const content = this.renderer.createElement('div');

        const embeddedView = this.infoWindowTemplate.createEmbeddedView({
            data: complaintAppointment
        });
        this.renderer.appendChild(content, embeddedView.rootNodes[0]);
        embeddedView.detectChanges();

        return content;
    }

    updateMapMarkers() {
        if (!this.complaintsMap || !this.apiLoaded) {
            return;
        }
        const map = this.complaintsMap.googleMap;

        const oms = new OverlappingMarkerSpiderfier(map, {
            markersWontMove: true,
            markersWontHide: true,
            basicFormatEvents: true,
            keepSpiderfied: true,
            legWeight: 2.5,
            circleFootSeparation: 50,
        });

        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
        }

        this.markers.forEach(marker => marker.setMap(null));
        this.markers = [];

        this.markers = this.complaintAppointmentsWithCoords.map(({ complaintAppointment, location }) => {
            const infoWindow = new google.maps.InfoWindow({
                content: this.buildContent(complaintAppointment),
                ariaLabel: 'Complaint',
                headerContent: this.buildHeader(),
            });

            const marker = new google.maps.Marker({
                position: location,
                map,
            });

            google.maps.event.addListener(marker, 'spider_click', () => {
                infoWindow.open({
                    anchor: marker,
                    map,
                });
            });

            oms.addMarker(marker, () => {
                infoWindow.open({
                    anchor: marker,
                    map,
                });
            });

            return marker;
        });

        this.markerCluster = new MarkerClusterer({
            map,
            markers: this.markers,
        });
    }

    open(docId: string, appointmentUuid: string) {
        this.complaintService.openDocumentByUuid(docId);
        this.router.navigate(['trade', 'complaint', docId], { queryParams: { origin: 'appointment-map', appointmentUuid } });
    }

    buildHeader(): HTMLElement {
        const infoWindowHeader = document.createElement('span');
        infoWindowHeader.className = 'complaint-info__header';
        infoWindowHeader.innerHTML = this.translateService.instant('COMPLAINT_APPOINTMENT|Szczegóły wizyty');

        return infoWindowHeader;
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage');
    }

    getWorkerName(id: number): string {
        return this.personsService.getWorkerByUserId(id)?.person.full_formal_name ?? '';
    }

    onCenterChanged() {
        const mapCenter = this.complaintsMap?.getCenter()?.toJSON();
        sessionStorage.setItem(`complaints-appointments-map-center-${
             this.configService?.user?.user?.id
         }`, JSON.stringify(mapCenter));
     }

     onZoomChanged() {
         const mapZoom = this.complaintsMap?.getZoom();
         sessionStorage.setItem(`complaints-appointments-map-zoom-${
             this.configService?.user?.user?.id
         }`, JSON.stringify(mapZoom));
     }
}
